import { IInventoryPurchaseOrderProductSearchResponseData } from "@/api_framework/api_modals/Inventory";
import { useInventoryProductSearchAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryProductSearchAPI";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "@uidotdev/usehooks";
import clsx from "clsx";
import { debounce } from "lodash";
import { Fragment, useEffect, useState } from "react";

const InventoryProductSearchComponent: React.FC<{
  flexibleAdd: boolean;
  showSelected?: boolean;
  containerClassName?: string;
  placeholder?: string;
  inputContainerClassName?: string;
  itemActiveClassName?: string;
  variant_category_id?: string;
  type?: string; // This is passed as a prop
  isInputEnabled?: boolean;
  clinic_id?: string;
  onSelectItem?: (item: IInventoryPurchaseOrderProductSearchResponseData) => void;
}> = ({ containerClassName, inputContainerClassName, onSelectItem, variant_category_id, type, clinic_id, ...props }) => {
  const { searchResults, execute: searchInSection, clearSearchResults } = useInventoryProductSearchAPI();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);

  const debounceSearch = debounce(() => {
    // if (variant_category_id) {
    searchInSection(debouncedQuery, variant_category_id, "PO", type, clinic_id);
    // }
  }, 500);

  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      debounceSearch();
    }
  }, [debouncedQuery, variant_category_id, type, clinic_id]);

  const reducedSearchResults = searchResults?.reduce((final, current) => {
    final.push({
      ...current,
    });
    return final;
  }, [] as IInventoryPurchaseOrderProductSearchResponseData[]);

  return (
    <div className={clsx(containerClassName)}>
      <Combobox
        onChange={(item) => {
          if (onSelectItem) {
            setQuery("");
            clearSearchResults();
            onSelectItem(item as IInventoryPurchaseOrderProductSearchResponseData);
          }
        }}
      >
        <div className="relative mt-1 w-full">
          <div className="relative border-gray-200 bg-white shadow-sm border rounded-lg focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 w-full text-left sm:text-sm cursor-default overflow-hidden focus:outline-none">
            <div className="flex justify-start items-center">
              {query?.length >= 3 ? (
                <button
                  className="p-2"
                  onClick={() => {
                    setQuery("");
                    clearSearchResults();

                    if (onSelectItem) {
                      onSelectItem(null);
                    }
                  }}
                >
                  <XMarkIcon className="w-4 h-4 text-red-500" />
                </button>
              ) : (
                <span className="p-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-500" />
                </span>
              )}
              <Combobox.Button className={clsx(inputContainerClassName)}>
                <Combobox.Input
                  autoComplete="off"
                  autoCorrect="off"
                  value={query}
                  placeholder={props?.placeholder ?? "Start Searching ..."}
                  className="py-2 pr-10 pl-3 border-none focus:ring-0 w-full text-gray-900 text-sm leading-5"
                  onChange={(event) => setQuery(event.target.value)}
                  onBlur={() => onSelectItem(null)} // Prevents auto-selection
                  readOnly={props?.isInputEnabled}
                />
              </Combobox.Button>
            </div>

            <Combobox.Button className="right-0 absolute inset-y-0 flex items-center pr-2">
              <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Combobox.Options className="z-30 absolute bg-white shadow-lg mt-1 py-1 rounded-md ring-1 ring-black/5 w-full max-h-60 text-base overflow-auto focus:outline-none sm:text-sm">
              <div className="pl-4 w-full">
                {props.isInputEnabled ? (
                  <p className="py-3 font-medium text-red-400 text-xs">Please Select Clinic Before Product Search</p>
                ) : (
                  <span className="text-xs">
                    Search results for <span className="font-semibold text-blue-600">{query}</span>
                  </span>
                )}
              </div>
              {reducedSearchResults?.length === 0 && query !== "" ? (
                <div className="relative px-4 py-2 text-gray-700 cursor-default select-none">
                  <p>No Result Found.</p>
                </div>
              ) : (
                reducedSearchResults?.map((result, idx) => (
                  <Combobox.Option
                    key={`${result}:${idx}`}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active
                          ? props?.itemActiveClassName
                            ? props?.itemActiveClassName
                            : "bg-teal-500 text-white"
                          : "text-gray-900"
                      }`
                    }
                    value={result}
                  >
                    {({ selected, active }) => (
                      <div className="flex justify-between items-center">
                        <span data-tooltip-content={result?.variant_name} className={`block   `}>
                          {result.variant_name}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-teal-600"
                            }`}
                          >
                            <CheckIcon className="w-5 h-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default InventoryProductSearchComponent;
